import Colors from "Constants/Color";
import { useState, useEffect } from "react";
import { Modal, Table } from "react-bootstrap";
import ActionButton from "components/Buttons/ActionButton";
import QRScanner from "utils/QRScanner";
import { toast } from "react-toastify";
import CancelBackButton from "components/Buttons/CancelBackButton";
import { tableStyles } from "Constants/Style";

interface StockTransferBag {
  inventory_item_id: string | number;
  bag_no: string;
  total_quantity: number;
  issued_quantity: number;
  status?: "verified" | "not_verified" | null;
}

interface StockTransferBagsModalProps {
  show: boolean;
  handleClose: () => void;
  bags: StockTransferBag[];
  itemIndex: number | null;
  verifiedBags: number[];
  onBagsVerified: (
    itemIndex: number,
    verifiedBagIndices: number[],
    updatedBags: StockTransferBag[]
  ) => void;
}

const StockTransferBagsModal: React.FC<StockTransferBagsModalProps> = ({
  show,
  handleClose,
  bags = [],
  itemIndex,
  verifiedBags = [],
  onBagsVerified,
}) => {
  const [bagStatuses, setBagStatuses] = useState<
    Record<number, "verified" | "not_verified" | null>
  >({});
  const [showScanner, setShowScanner] = useState(false);
  const [currentBagIndex, setCurrentBagIndex] = useState<number | null>(null);

  // Initialize bag statuses from bags array and verifiedBags prop
  useEffect(() => {
    const initialStatuses: Record<number, "verified" | "not_verified" | null> =
      {};

    // First, set statuses from bags array if they exist
    bags.forEach((bag, idx) => {
      if (bag.status) {
        initialStatuses[idx] = bag.status;
      }
    });

    // Then, set verified statuses from verifiedBags prop
    if (verifiedBags.length > 0) {
      verifiedBags.forEach((bagIdx) => {
        initialStatuses[bagIdx] = "verified";
      });
    }

    setBagStatuses(initialStatuses);
  }, [verifiedBags, show, bags]);

  const handleScanClick = (bagIndex: number) => {
    setCurrentBagIndex(bagIndex);
    setShowScanner(true);
  };

  const handleScanResult = (scannedValue: any) => {
    if (currentBagIndex === null) return;

    const bag = bags[currentBagIndex];
    let scannedId: string | number;

    // Extract ID from scanned value
    if (typeof scannedValue === "string" || typeof scannedValue === "number") {
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(String(scannedValue));
        scannedId = parsed.inventory_item_id || parsed.id || scannedValue;
      } catch {
        // Direct string or number value
        scannedId = scannedValue;
      }
    } else if (typeof scannedValue === "object" && scannedValue !== null) {
      // Try to get ID from object (could be inventory_item_id, id, etc.)
      scannedId =
        scannedValue.inventory_item_id || scannedValue.id || scannedValue;
    } else {
      scannedId = scannedValue;
    }

    // Convert both to strings for comparison (trim whitespace)
    const bagInventoryId = String(bag.inventory_item_id).trim();
    const scannedIdStr = String(scannedId).trim();

    // Compare scanned ID with inventory_item_id
    const newStatus: "verified" | "not_verified" =
      bagInventoryId === scannedIdStr ? "verified" : "not_verified";

    setBagStatuses((prev) => ({
      ...prev,
      [currentBagIndex]: newStatus,
    }));

    // Update the bag in the bags array with status
    if (newStatus === "verified") {
      toast.success("✅ Bag verified successfully!");
    } else {
      // toast.error(
      //   `❌ Bag verification failed! Expected: ${bagInventoryId}, Scanned: ${scannedIdStr}`
      // );
      toast.error(
        `❌ Bag verification failed! Wrong Bag Scanned.`
      );
    }

    setShowScanner(false);
    setCurrentBagIndex(null);
  };

  const handleDoneClick = () => {
    if (itemIndex === null) {
      toast.error("Invalid item index");
      return;
    }

    // Check if all bags are verified
    const allBagsVerified = bags.every((bag, idx) => {
      const status = bagStatuses[idx] || bag.status;
      return status === "verified";
    });

    if (!allBagsVerified) {
      toast.error("Please verify all bags before proceeding");
      return;
    }

    // Get all verified bag indices and update bags with status
    const updatedBags = bags.map((bag, idx) => {
      const status = bagStatuses[idx] || bag.status;
      return {
        ...bag,
        status: status || null,
      };
    });

    const verifiedBagIndices = updatedBags
      .map((bag, idx) => {
        return bag.status === "verified" ? idx : null;
      })
      .filter((idx) => idx !== null) as number[];

    // Call the callback to update parent state with verified indices and updated bags
    onBagsVerified(itemIndex, verifiedBagIndices, updatedBags);
    toast.success("All bags verified successfully!");
    handleClose();
  };

  const getStatusBadge = (
    status: "verified" | "not_verified" | null | undefined
  ) => {
    if (!status) {
      return (
        <span
          style={{
            backgroundColor: Colors?.bg_gray,
            color: "white",
            padding: "2px 8px",
            borderRadius: "4px",
            fontSize: "11px",
            textTransform: "capitalize",
          }}
        >
          Not Scanned
        </span>
      );
    }

    const isVerified = status === "verified";
    return (
      <span
        style={{
          backgroundColor: isVerified ? Colors?.bg_green : Colors?.bg_damage,
          color: "white",
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "11px",
          textTransform: "capitalize",
        }}
      >
        {isVerified ? "Verified" : "Not Verified"}
      </span>
    );
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered size="lg">
        <Modal.Header className="border-0">
          <Modal.Title className="fw-bold">Stock Transfer Bags</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleClose}
          />
        </Modal.Header>
        <hr />
        <Modal.Body>
          {bags.length === 0 ? (
            <div className="text-center py-4 text-muted">No bags available.</div>
          ) : (
            <Table responsive className="align-middle text-center">
              <thead>
                <tr>
                  <th style={{ ...tableStyles.headerAlt, minWidth: 70 }}>
                    Sr No.
                  </th>
                  <th style={{ ...tableStyles.headerAlt, minWidth: 150 }}>
                    Bag No.
                  </th>
                  <th style={{ ...tableStyles.headerAlt, minWidth: 100 }}>
                    Total Quantity
                  </th>
                  <th style={{ ...tableStyles.headerAlt, minWidth: 100 }}>
                    Issued Quantity
                  </th>
                  <th style={{ ...tableStyles.headerAlt, minWidth: 150 }}>
                    Status
                  </th>
                  <th
                    style={{
                      ...tableStyles.headerAlt,
                      ...tableStyles.actionHeader,
                      minWidth: 150,
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {bags.map((bag, idx) => (
                  <tr key={idx} style={tableStyles.rowHover}>
                    <td style={tableStyles.cell}>{idx + 1}</td>
                    <td style={tableStyles.cell}>{bag.bag_no || "-"}</td>
                    <td style={tableStyles.cell}>{bag.total_quantity || "-"}</td>
                    <td style={tableStyles.cell}>{bag.issued_quantity || "-"}</td>
                    <td style={tableStyles.cell}>
                      {getStatusBadge(bagStatuses[idx] || bag.status || null)}
                    </td>
                    <td style={{ ...tableStyles.cell, ...tableStyles.actionCell }}>
                      <div style={tableStyles.actionButtonContainer}>
                        <ActionButton
                          label="Scan"
                          icon="bi bi-qr-code-scan"
                          handleClick={() => handleScanClick(idx)}
                          variant="primary"
                          style={{ width: "auto", minWidth: "80px" }}
                          disabled={bagStatuses[idx] === "verified" || bag.status === "verified"}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <div className="w-100 d-flex justify-content-center gap-2">
            <CancelBackButton label="Cancel" onClick={handleClose} />
            <ActionButton
              label="Done"
              variant="primary"
              handleClick={handleDoneClick}
            />
          </div>
        </Modal.Footer>
      </Modal>

      <QRScanner
        show={showScanner}
        handleClose={() => {
          setShowScanner(false);
          setCurrentBagIndex(null);
        }}
        onResult={handleScanResult}
      />
    </>
  );
};

export default StockTransferBagsModal;

